import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage, intlShape } from 'react-intl';

import { Link } from 'react-router-dom';
import {
  Button,
  Card,
  Icon,
  ProgressRing,
  ToastContainer
} from '@salesforce/design-system-react';

import {
  AdvancedSettingStep,
  DataExtensionToast,
  DESelectionModal,
  ImportProfileWizard
} from './';

import './AdvancedSettings.scss';
import { default as ActionCable } from '../../utilities/actionCable';
import * as Util from '../../common/utilities';

class AdvancedSettings extends Component {
  componentDidMount() {
    const importType = this.isFTP() ? 'FtpImport' : 'DeImport';
    this.props.actions.getImportConfiguration(importType);
    this.props.actions.getAttributes();
    this.getImports();
    this.actionCable = new ActionCable('metalMastodon', 'ecs/cable');
    this.actionCable.subscribe(
      'ImportWorkerChannel',
      {},
      this.onReceived.bind(this)
    );
  }

  componentDidUpdate(prevProps) {
    if (
      Util.propChanged(
        prevProps,
        this.props,
        'imports.data.lastDeImport',
        'error'
      ) ||
      Util.propChanged(
        prevProps,
        this.props,
        'imports.data.lastDeImport',
        'complete_with_error'
      )
    ) {
    }
  }

  onReceived(data) {
    this.getImports();
  }

  getImports() {
    const options = { ...this.props.location.query };
    this.props.actions.getImports(options);
  }

  isFTP() {
    return this.props?.flags?.ftpImport || this.props?.flags?.flags?.ftpImport;
  }

  openProfileDESectionModal() {
    if (!this.props.dataExtensions?.data?.data_extensions) {
      this.props.actions.getDataExtensions();
    }

    // load things in case they have changed without revisiting this page
    this.props.actions.getAttributes();
    const importType = this.isFTP() ? 'FtpImport' : 'DeImport';
    this.props.actions.getImportConfiguration(importType);

    // if no previous de import exists, open the profile import wizard
    if (this.previousDataExtension() === undefined) {
      this.openProfileImportWizard();
    } else {
      this.props.actions.openModal('profileDESelection');
    }
  }

  previousDataExtension() {
    const sourceInfo = this.props?.importConfiguration?.data
      ?.import_configuration?.source_info;
    if (sourceInfo === undefined) {
      return undefined;
    } else {
      return {
        ...sourceInfo,
        customer_key: sourceInfo.customer_de_key,
        sendable_data_extension_field: { name: sourceInfo.subscriber_key }
      };
    }
  }

  profileAttributes() {
    const profileAttributes =
      this.props?.attributes?.data?.profileAttributes || [];
    if (profileAttributes.length > 0) {
      return this.enhancedProfileAttributes(profileAttributes);
    }
    return this.enhancedProfileAttributes(this.legacyProfileAttributes());
  }

  // TODO: remove this fallback after a reasonable amount of time
  legacyProfileAttributes() {
    const importColumns =
      this.props?.imports?.data?.lastDeImport?.source_info?.import_columns ||
      [];
    const enhancedImportColumns = importColumns.map(function(c) {
      const dataType = (c.data_type || c.field_type || 'text').toLowerCase();
      const values =
        dataType.toLowerCase() === 'boolean' ? ['True', 'False'] : undefined;
      return {
        name: c.name,
        data_type: dataType,
        values
      };
    });
    return enhancedImportColumns;
  }

  enhancedProfileAttributes(attrs) {
    const subscriberKey = this.previousDataExtension()
      ?.sendable_data_extension_field?.name;
    return attrs.map((attr, i) => ({
      id: `${attr.name}`,
      label: attr.name,
      locked: attr.name === subscriberKey,
      ...attr
    }));
  }

  // TODO: duplicated code in ProfileAttributeList
  openProfileImportWizard() {
    const importConfiguration = this.props.importConfiguration?.data
      ?.import_configuration;

    if (!this.props.dataExtensions?.data?.data_extensions) {
      this.props.actions.getDataExtensions();
    }

    if (importConfiguration?.source_info) {
      const sourceInfo = importConfiguration?.source_info;
      const dataExtension = {
        ...sourceInfo,
        customer_key: sourceInfo.customer_de_key,
        sendable_data_extension_field: { name: sourceInfo.subscriber_key }
      };

      const profileAttributes = this.profileAttributes();

      // Load DE details if we have a previous import
      this.props.actions.getDataExtensionColumns(dataExtension);

      this.props.actions.openModal('profileImport', {
        step1: { completed: true, dataExtension: dataExtension },
        step2: {
          completed: true,
          schedule: {
            ...importConfiguration.schedule,
            importRecurring: importConfiguration?.schedule?.sync_days > 0
          },
          notification_info: importConfiguration?.notification_info || {}
        },
        step3: { selectedAttributes: profileAttributes },
        step5: { profileAttributes: profileAttributes }
      });
    } else {
      this.props.actions.openModal('profileImport', {
        step1: { completed: false }
      });
    }
  }

  buildImportDetails(info) {
    if (!info) {
      return null;
    }

    const { formatMessage, formatNumber } = this.props.intl;
    let syncedProfiles = '';

    if (info.imported_rows && info.total_rows) {
      syncedProfiles = formatMessage(
        {
          id:
            'components.ecs.advancedSettings.dataExtensionSync.syncedProfilesCounts'
        },
        {
          imported: formatNumber(info.imported_rows),
          total: formatNumber(info.total_rows)
        }
      );
    }

    return `${info.name}. ${syncedProfiles}`;
  }

  lastImportBadge() {
    const { formatMessage } = this.props.intl;
    const { lastDeImport } = this.props.imports.data;

    if (lastDeImport) {
      const { source_info, status } = lastDeImport;
      const badgeFor = status => {
        switch (status) {
          case 'importing':
            return 'slds-theme_inverse';
          case 'complete':
            return 'slds-theme_success';
          case 'error':
            return 'slds-theme_error';
          case 'complete_with_error':
            return 'slds-theme_warning';
          default:
            return '';
        }
      };
      const statusText = status => {
        return status
          ? formatMessage({ id: `components.ecs.profiles.statuses.${status}` })
          : null;
      };

      const progressRing = status => {
        switch (status) {
          case 'importing':
            const progress = source_info.progress_percentage || 0;
            return <ProgressRing value={progress} />;
          default:
            return null;
        }
      };

      return (
        <div className="slds-m-top_x-small">
          <span className={`slds-badge ${badgeFor(status)}`}>
            {statusText(status)}
          </span>
          <span className="slds-m-left_x-small">{progressRing(status)}</span>
          <span className="slds-m-left_x-small">
            {this.buildImportDetails(source_info)}
          </span>
        </div>
      );
    }
    return null;
  }

  renderProfileDataExtensionSection() {
    const { formatMessage } = this.props.intl;

    return (
      <div>
        <span>
          {formatMessage({
            id: 'components.ecs.advancedSettings.dataExtensionSync.description'
          })}
        </span>
        {this.lastImportBadge()}
      </div>
    );
  }

  profileHistoryLink() {
    const { formatMessage } = this.props.intl;
    const { lastDeImport } = this.props.imports.data;
    if (lastDeImport) {
      return (
        <Link to={{ pathname: '/profiles' }} className="nav-link">
          <Button
            className="edit-settings"
            variant="neutral"
            label={formatMessage({
              id: 'components.ecs.advancedSettings.dataExtensionSync.history'
            })}
          />
        </Link>
      );
    }
    return null;
  }

  importAllowed(flags) {
    const { lastDeImport } = this.props.imports.data;
    const importConfigLoading = this.props.importConfiguration?.isLoading;

    return (
      !importConfigLoading &&
      !flags.isReadonly &&
      !['importing', 'queued'].includes(lastDeImport?.status)
    );
  }

  renderWeatherAttributeWarning() {
    if (
      this.props.rules?.data?.rules?.settings?.weather &&
      Object.keys(this.props.rules?.data?.rules?.location_mapping || {}) < 1
    ) {
      const helpLink =
        'https://help.salesforce.com/s/articleView?id=sf.mc_pb_manage_profile_attributes.htm&type=5';
      return (
        <div className="warning-box">
          <Icon
            assistiveText={{ label: 'Info' }}
            className="warning-icon"
            category="utility"
            name="info"
            size="medium"
          />
          <span className="warning-label">
            <FormattedMessage
              id="components.ecs.advancedSettings.profileAttributes.weatherAttributeInfo"
              values={{
                link: (
                  <a rel="noopener noreferrer" target="_blank" href={helpLink}>
                    {this.props.intl.formatMessage({
                      id: 'components.ecs.nocatalog.helpLinkText'
                    })}
                  </a>
                )
              }}
            />
          </span>
        </div>
      );
    }
  }

  render() {
    const { formatMessage } = this.props.intl;
    const { flags } = this.props;
    const cardHeader = (
      <header className="advanced-settings-header">
        <h1>
          <FormattedMessage id="components.ecs.advancedSettings.heading.title" />
        </h1>
        <p>
          <FormattedMessage id="components.ecs.advancedSettings.heading.description" />
        </p>
      </header>
    );
    return (
      <Card
        className="advanced-settings"
        bodyClassName="advanced-settings-body"
        heading={cardHeader}
      >
        <div>
          <AdvancedSettingStep
            iconName="category"
            iconColor="navy"
            title={formatMessage({
              id: 'components.ecs.advancedSettings.classes.title'
            })}
            description={formatMessage({
              id: 'components.ecs.advancedSettings.classes.description'
            })}
            rightNav={
              <Link to={{ pathname: '/classes' }}>
                <Button
                  className="edit-settings"
                  variant="neutral"
                  label={formatMessage({
                    id: 'components.ecs.advancedSettings.classes.rightNav'
                  })}
                />
              </Link>
            }
          />
          <AdvancedSettingStep
            iconName="text"
            iconColor="sky"
            title={formatMessage({
              id: 'components.ecs.advancedSettings.assetAttributes.title'
            })}
            description={formatMessage({
              id: 'components.ecs.advancedSettings.assetAttributes.description'
            })}
            rightNav={
              <Link to={{ pathname: '/attributes/asset' }}>
                <Button
                  className="edit-settings"
                  variant="neutral"
                  label={formatMessage({
                    id:
                      'components.ecs.advancedSettings.assetAttributes.rightNav'
                  })}
                />
              </Link>
            }
          />
          <AdvancedSettingStep
            iconName="text"
            iconColor="sky"
            title={formatMessage({
              id: 'components.ecs.advancedSettings.profileAttributes.title'
            })}
            description={formatMessage({
              id:
                'components.ecs.advancedSettings.profileAttributes.description'
            })}
            rightNav={
              <Link to={{ pathname: '/attributes/profile' }}>
                <Button
                  className="edit-settings"
                  variant="neutral"
                  label={formatMessage({
                    id:
                      'components.ecs.advancedSettings.profileAttributes.rightNav'
                  })}
                />
              </Link>
            }
            children={this.renderWeatherAttributeWarning()}
          />
          <AdvancedSettingStep
            iconName="groups"
            iconColor="pink"
            title={formatMessage({
              id: 'components.ecs.advancedSettings.dataExtensionSync.title'
            })}
            description={this.renderProfileDataExtensionSection()}
            rightNav={
              <React.Fragment>
                <Button
                  id="sync-data-extension"
                  variant="neutral"
                  disabled={!this.importAllowed(flags)}
                  label={formatMessage({
                    id:
                      'components.ecs.advancedSettings.dataExtensionSync.rightNav'
                  })}
                  onClick={this.openProfileDESectionModal.bind(this)}
                />
                {this.profileHistoryLink()}
              </React.Fragment>
            }
          />
          <AdvancedSettingStep
            iconName="formula"
            iconColor="green"
            title={formatMessage({
              id: 'components.ecs.advancedSettings.contentRules.title'
            })}
            description={formatMessage({
              id: 'components.ecs.advancedSettings.contentRules.description'
            })}
            rightNav={
              <Link to={{ pathname: '/rules' }}>
                <Button
                  className="edit-settings"
                  variant="neutral"
                  label={formatMessage({
                    id: 'components.ecs.advancedSettings.contentRules.rightNav'
                  })}
                />
              </Link>
            }
          />
          <AdvancedSettingStep
            iconName="photo"
            iconColor="yellow"
            title={formatMessage({
              id: 'components.ecs.advancedSettings.fallbackAssets.title'
            })}
            description={formatMessage({
              id: 'components.ecs.advancedSettings.fallbackAssets.description'
            })}
            rightNav={
              <Link to={{ pathname: '/fallback' }}>
                <Button
                  className="edit-settings"
                  variant="neutral"
                  label={formatMessage({
                    id:
                      'components.ecs.advancedSettings.fallbackAssets.rightNav'
                  })}
                />
              </Link>
            }
          />
        </div>
        <DESelectionModal
          actions={this.props.actions}
          modals={this.props.modals}
          attributes={this.props.attributes}
          dataExtensions={this.props.dataExtensions}
          rules={this.props.rules}
          imports={this.props.imports}
          importConfiguration={this.props.importConfiguration}
          onEdit={this.openProfileImportWizard.bind(this)}
          flags={flags}
        />
        <ImportProfileWizard
          actions={this.props.actions}
          modals={this.props.modals}
          attributes={this.props.attributes}
          dataExtensions={this.props.dataExtensions}
          rules={this.props.rules}
          importConfiguration={this.props.importConfiguration}
          flags={flags}
          maxAttributes={30}
        />
        <ToastContainer>
          <DataExtensionToast
            actions={this.props.actions}
            dataExtensions={this.props.dataExtensions}
            toast={this.props.toast}
          />
        </ToastContainer>
      </Card>
    );
  }
}

AdvancedSettings.propTypes = {
  actions: PropTypes.object.isRequired,
  dataExtensions: PropTypes.object.isRequired,
  flags: PropTypes.object.isRequired,
  imports: PropTypes.object.isRequired,
  importConfiguration: PropTypes.object.isRequired,
  modals: PropTypes.object.isRequired,
  rules: PropTypes.object.isRequired,
  toast: PropTypes.object,
  location: PropTypes.object,
  intl: intlShape.isRequired
};

export default injectIntl(AdvancedSettings);
